/**
 * Privacy Policy Page
 * 
 * Legal document explaining how BRV Labs collects, uses, and protects user information.
 * This page is accessible via the footer and is designed for readability and compliance.
 */

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-forest-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600">Effective Date: December 4, 2025</p>
        </div>

        {/* Legal Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <p className="text-gray-700 leading-relaxed">
              BRV Labs ("BRV Labs," "we," "us," or "our") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have. Our services include interactive books, micro-lessons, animated videos, educational activity books, mediation services, and college advising (collectively, the "Services").
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Who May Create Accounts</h2>
              <p className="text-gray-700 leading-relaxed">
                Accounts may be created only by educators, parents, or legal guardians. Children access BRV Labs content solely under adult-managed accounts and supervision.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect only what's necessary to provide and improve the Services.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Adult Account Information:</strong> name, email, password, role (e.g., parent, educator), and contact preferences.</li>
                <li><strong>Purchase and Billing:</strong> shipping address (for physical products), transaction details, and limited payment metadata. Payment card details are processed by a third-party payment processor and are not stored by BRV Labs.</li>
                <li><strong>Child Use Data (Minors):</strong> limited, non-identifying engagement metrics (e.g., lesson completions, time-on-task, feature usage), and choices made within activities. We do not require children to submit personal information.</li>
                <li><strong>Support and Communications:</strong> messages, feedback, and survey responses.</li>
                <li><strong>Device and Usage Data:</strong> IP address, device/browser type, pages viewed, general event logs, and error diagnostics. This supports security, fraud prevention, and performance.</li>
                <li><strong>Cookies and Similar Technologies:</strong> used for core functionality (e.g., session management) and, where permitted, for analytics. You can control cookies via browser settings.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Information</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Provide, maintain, and improve the Services.</li>
                <li>Personalize experiences (e.g., resume progress, highlight activities).</li>
                <li>Process transactions and deliver products.</li>
                <li>Communicate about accounts, security, updates, and support.</li>
                <li>Ensure safety, integrity, and compliance with law.</li>
                <li>De-identify and aggregate usage data for research and product improvement.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Children's Privacy</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Adult-Managed Access:</strong> Accounts are created and controlled by adults. Children use content only under adult supervision.</li>
                <li><strong>Minimal Collection:</strong> We avoid collecting personal information from children. Activity data is limited to engagement metrics and in-experience choices.</li>
                <li><strong>No Behavioral Advertising to Children:</strong> We do not serve interest-based ads to children or build profiles for advertising.</li>
                <li><strong>Legal Compliance:</strong> We comply with applicable child privacy laws (e.g., COPPA in the U.S.) and work with schools to align with FERPA where relevant.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Legal Bases (EEA/UK, where applicable)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">We process personal data based on:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Contract:</strong> to deliver the Services you requested.</li>
                <li><strong>Legitimate Interests:</strong> to secure, improve, and personalize the Services.</li>
                <li><strong>Consent:</strong> where required for optional features (e.g., certain analytics or communications).</li>
                <li><strong>Legal Obligation:</strong> to meet regulatory or law-enforcement requirements.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell personal information.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">We share information only with:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Service Providers:</strong> payment processing, hosting, analytics, customer support, email delivery, and shipping—bound by contractual confidentiality and security obligations.</li>
                <li><strong>Educational Partners:</strong> schools/districts using our Services to support learning; access is limited to authorized personnel.</li>
                <li><strong>Legal/Compliance:</strong> to protect safety, rights, or comply with law, subpoenas, or court orders.</li>
                <li><strong>Business Transfers:</strong> if we undergo a merger, acquisition, or asset transfer, data may be transferred consistent with this Policy.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain information only as long as necessary to provide the Services, comply with law, resolve disputes, and enforce agreements. When no longer needed, we delete or de-identify data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We use reasonable administrative, technical, and physical safeguards to protect information, including encryption in transit, access controls, and monitoring. No system is 100% secure; please protect your account credentials and notify us of any suspected misuse.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Your Rights and Choices</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have rights to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Access, correct, or delete your personal information.</li>
                <li>Object to or restrict certain processing.</li>
                <li>Withdraw consent where processing is based on consent.</li>
                <li>Data portability (receive a copy in a usable format).</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise rights, contact us (Section 12). For school-managed data, requests should be directed through the educational institution.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                If we transfer data across borders, we use lawful mechanisms (e.g., EU Standard Contractual Clauses) and safeguard your information per applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. School and District Use</h2>
              <p className="text-gray-700 leading-relaxed">
                When used by an educational institution, BRV Labs acts as a processor/service provider for student data per contract. The institution is the controller and manages consent, access, and deletion. We process such data only on documented instructions and do not use it for advertising.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                Privacy questions, requests, or complaints:
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:admin@brvlabsbooks.co" className="text-forest-600 hover:text-forest-700 underline">
                  admin@brvlabsbooks.co
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Third-Party Tools and Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our Services may integrate third-party tools (e.g., payment, hosting, analytics). Their privacy practices are governed by their policies. We recommend reviewing them before use.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy to reflect operational, legal, or regulatory changes. If we make material changes, we will notify you by posting the updated Policy or via other reasonable means. Continued use after changes indicates acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. California Notice (CCPA/CPRA)</h2>
              <p className="text-gray-700 leading-relaxed">
                We do not sell or share personal information for cross-context behavioral advertising. California residents can exercise rights to know, delete, correct, and limit use of sensitive information by contacting us (Section 12).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Data Protection Officer/Representative</h2>
              <p className="text-gray-700 leading-relaxed">
                If required by law in your jurisdiction and once appointed, we will provide contact details here.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Scope</h2>
              <p className="text-gray-700 leading-relaxed">
                This Policy applies to all BRV Labs products and services branded or operated by BRV Labs, unless a product lists a separate privacy notice. In case of conflict, product-specific notices control.
              </p>
            </section>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="mt-12 text-center">
          <Link to="/">
            <Button className="bg-forest-600 hover:bg-forest-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
